// src/pages/DeliveryPortal.js — PUBLIC page at /delivery/<token>. No login, no Supabase session.
// Lists the delivery's files; downloads one via /api/delivery-download-sign, or MANY via the batch endpoint
// zipped client-side (client-zip). ⛔ The client only ever sees file id + name + size — never a storage_path.
//
// ⛔ NOTHING FAILS SILENTLY HERE. CBREIM (2026-10-02): three recipients opened the page and not one download
// request ever reached us — and nothing on the page or in Access History said why. Now every failure (a request that
// never reaches us, a refused or failed sign, a file the browser can't fetch, a script error) does two things:
//   1. shows the recipient a plain message (and the page never sits on "Loading…" or "Preparing…" forever);
//   2. reports to /api/delivery-client-error with browser details → a 'client_error' row in Access History.
// A single file is fetched here and saved from a same-origin blob (like the zip and Intake's download), so a
// storage failure is SEEN; if that fetch fails, the page reports it and falls back to opening the link directly.
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { downloadZip } from 'client-zip';

const UNAVAILABLE_MSG = 'This link is no longer available — please contact your NTRG representative.';
const CONTACT = 'If it keeps happening, please contact your NTRG representative.';
const NETWORK_MSG = `We couldn't reach NTRG's server from this network. Please check your connection and try again. ${CONTACT}`;
const RATE_MSG = 'Too many downloads in the last hour — please wait a little and try again.';
const FETCH_MSG = `The file couldn't be downloaded — your network may be blocking our file storage (r2.cloudflarestorage.com). We've tried opening it directly instead. ${CONTACT}`;
const MAX_ZIP_BYTES = 500 * 1048576;   // ⛔ pre-flight ceiling — refuse (don't hang the tab) above this
const MAX_REPORTS = 20;                // per page load — a looping failure must not spam the log
const fmtSize = (b) => b == null ? '' : b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

// Never throws: a request that doesn't reach us comes back as status 0 / error 'network'.
async function post(path, body) {
  let resp;
  try { resp = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) }); }
  catch (e) { return { ok: false, status: 0, json: { error: 'network' }, message: e && e.message }; }
  const json = await resp.json().catch(() => ({}));
  return { ok: resp.ok, status: resp.status, json };
}

const browserInfo = () => {
  const info = {};
  try {
    info.language = navigator.language; info.online = navigator.onLine; info.cookies = navigator.cookieEnabled;
    info.viewport = `${window.innerWidth}x${window.innerHeight}`; info.screen = `${window.screen.width}x${window.screen.height}`;
    info.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; info.page_age_s = Math.round(performance.now() / 1000);
  } catch { /* best-effort */ }
  return info;
};

// Saves a blob under its real name (same-origin blob URL, so the download attribute is honoured).
function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export default function DeliveryPortal({ token }) {
  const [state, setState] = useState('loading');      // 'loading' | 'ready' | 'unavailable' | 'error'
  const [loadMsg, setLoadMsg] = useState(null);
  const [label, setLabel] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState({});               // file_id -> true (single download in flight)
  const [fileMsg, setFileMsg] = useState({});         // file_id -> message shown under that file
  const [selected, setSelected] = useState(() => new Set());
  const [zipping, setZipping] = useState(false);
  const [zipProgress, setZipProgress] = useState({ done: 0, total: 0 });
  const [zipMsg, setZipMsg] = useState(null);
  const reports = useRef(0);

  // Fire-and-forget report of a browser-side failure → a 'client_error' row (api/delivery-client-error).
  const report = useCallback((info) => {
    if (reports.current >= MAX_REPORTS) return;
    reports.current += 1;
    try {
      fetch('/api/delivery-client-error', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
        body: JSON.stringify({ token, ...info, browser: browserInfo() }),
      }).catch(() => {});
    } catch { /* reporting must never break the page */ }
  }, [token]);

  // Script errors anywhere on this page (a broken click handler, a library throwing) are reported too.
  useEffect(() => {
    const onError = (e) => report({ stage: 'script', message: (e && (e.message || (e.error && e.error.message))) || 'error' });
    const onRejection = (e) => report({ stage: 'script', message: `unhandled: ${(e && e.reason && (e.reason.message || String(e.reason))) || 'rejection'}` });
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => { window.removeEventListener('error', onError); window.removeEventListener('unhandledrejection', onRejection); };
  }, [report]);

  const load = useCallback(async () => {
    setState('loading'); setLoadMsg(null);
    const r = await post('/api/delivery-view', { token });
    if (r.ok && r.json.available) { setLabel(r.json.label); setFiles(r.json.files || []); setState('ready'); return; }
    if (r.ok) { setState('unavailable'); return; }                     // expired / revoked / wrong link — audited server-side
    report({ stage: 'view', status: r.status, error: r.json.error, message: r.message });
    setLoadMsg(r.status === 0 ? NETWORK_MSG
      : r.status === 429 ? 'This link has been opened too many times in the last hour — please try again later.'
      : `Your files couldn't be loaded (error ${r.status}). Please try again. ${CONTACT}`);
    setState('error');
  }, [token, report]);
  useEffect(() => { load(); }, [load]);

  const setMsgFor = (id, msg) => setFileMsg((m) => { const n = { ...m }; if (msg) n[id] = msg; else delete n[id]; return n; });

  async function download(f) {
    if (busy[f.id]) return;                            // ⛔ in-flight guard → one click = one sign = one audit row
    setBusy((b) => ({ ...b, [f.id]: true }));
    setMsgFor(f.id, null);
    try {
      const r = await post('/api/delivery-download-sign', { token, file_id: f.id });
      if (!r.ok || !r.json.url) {
        if (r.json.error === 'unavailable') { setState('unavailable'); return; }   // audited server-side
        report({ stage: 'sign', status: r.status, error: r.json.error, message: r.message, file_id: f.id });
        setMsgFor(f.id, r.status === 0 ? NETWORK_MSG : r.json.error === 'rate_limited' ? RATE_MSG
          : `This file couldn't be prepared (error ${r.status}). Please try again. ${CONTACT}`);
        return;
      }
      // Fetch it here so a storage failure is seen; if it fails, report and fall back to the plain link.
      let blob = null, status = 0, message = null;
      try {
        const resp = await fetch(r.json.url);
        status = resp.status;
        if (resp.ok) blob = await resp.blob();
      } catch (e) { message = e && e.message; }
      if (blob) { saveBlob(blob, f.file_name); return; }
      report({ stage: 'fetch', status, message: message || `HTTP ${status}`, file_id: f.id });
      setMsgFor(f.id, FETCH_MSG);
      const a = document.createElement('a'); a.href = r.json.url; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      report({ stage: 'script', message: e && e.message, file_id: f.id });
      setMsgFor(f.id, `Something went wrong preparing this file. Please try again. ${CONTACT}`);
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[f.id]; return n; });
    }
  }

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = files.length > 0 && selected.size === files.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(files.map((f) => f.id)));
  const chosen = files.filter((f) => selected.has(f.id));
  const selectedBytes = chosen.reduce((n, f) => n + (f.size_bytes || 0), 0);
  const overLimit = selectedBytes > MAX_ZIP_BYTES;

  async function downloadSelected() {
    if (zipping || chosen.length === 0 || overLimit) return;
    setZipping(true); setZipProgress({ done: 0, total: chosen.length }); setZipMsg(null);
    try {
      const r = await post('/api/delivery-download-sign-batch', { token, file_ids: chosen.map((f) => f.id) });
      if (!r.ok || !r.json.files) {
        if (r.json.error === 'unavailable') { setState('unavailable'); return; }   // audited server-side
        report({ stage: 'batch', status: r.status, error: r.json.error, message: r.message });
        setZipMsg(r.status === 0 ? NETWORK_MSG : r.json.error === 'rate_limited' ? RATE_MSG
          : `The download couldn't be prepared (error ${r.status}). Please try again. ${CONTACT}`);
        return;
      }
      const entries = [], failed = [];
      let firstFailure = null;
      for (const item of r.json.files) {               // fetch each; a failure skips ONE, not the whole zip
        try {
          const resp = await fetch(item.url);
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          entries.push({ name: item.file_name, input: await resp.blob() });
        } catch (e) { failed.push(item.file_name); firstFailure = firstFailure || (e && e.message); }
        setZipProgress((p) => ({ done: p.done + 1, total: r.json.files.length }));
      }
      if (failed.length) report({ stage: 'fetch', message: firstFailure, files_failed: failed });
      if (entries.length === 0) { setZipMsg(`None of the files could be downloaded — your network may be blocking our file storage (r2.cloudflarestorage.com). ${CONTACT}`); return; }
      const zipName = `${(label || 'delivery').replace(/[^a-z0-9 _.-]/gi, '_').trim().slice(0, 80) || 'delivery'}.zip`;
      saveBlob(await downloadZip(entries).blob(), zipName);
      setZipMsg(failed.length
        ? `${entries.length} of ${r.json.files.length} included · ${failed.length} failed: ${failed.slice(0, 4).join(', ')}${failed.length > 4 ? '…' : ''}. ${CONTACT}`
        : `✓ ${entries.length} file${entries.length === 1 ? '' : 's'} zipped.`);
    } catch (e) {
      report({ stage: 'zip', message: e && e.message });
      setZipMsg('The zip could not be built — the selection may be too large. Try fewer files.');
    } finally {
      setZipping(false);
    }
  }

  const wrap = { minHeight: '100vh', background: '#f1f5f9', fontFamily: "'Public Sans',system-ui,sans-serif", color: '#0f172a', padding: '40px 16px' };
  const card = { maxWidth: '720px', margin: '0 auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' };

  if (state === 'loading') return <div style={wrap}><div style={card}><div style={{ color: '#64748b' }}>Loading…</div></div></div>;
  if (state === 'error') return <div style={wrap}><div style={card}><div style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>Couldn't load your files</div><div style={{ color: '#475569', fontSize: '14px', marginBottom: '14px' }}>{loadMsg}</div><button onClick={load} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Try again</button></div></div>;
  if (state === 'unavailable') return <div style={wrap}><div style={card}><div style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>Delivery unavailable</div><div style={{ color: '#475569', fontSize: '14px' }}>{UNAVAILABLE_MSG}</div></div></div>;

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '22px', fontWeight: 800, marginBottom: '2px' }}>NTRG — Document Delivery</div>
        <div style={{ color: '#475569', fontSize: '14px', marginBottom: '16px' }}>{label || 'Your files are ready to download.'} · {files.length} file{files.length === 1 ? '' : 's'}</div>

        {files.length === 0 ? <div style={{ color: '#94a3b8', fontSize: '14px' }}>No files in this delivery yet.</div> : (
          <>
            {/* selection toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} /> Select all
              </label>
              <span style={{ fontSize: '13px', color: overLimit ? '#dc2626' : '#475569' }}>
                {chosen.length} file{chosen.length === 1 ? '' : 's'} · {fmtSize(selectedBytes)} selected
                {overLimit && ' — too large to zip; download individually or fewer'}
              </span>
              <button onClick={downloadSelected} disabled={zipping || chosen.length === 0 || overLimit}
                style={{ marginLeft: 'auto', background: (zipping || chosen.length === 0 || overLimit) ? '#94a3b8' : '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 14px', fontSize: '13px', fontWeight: 600, cursor: (zipping || chosen.length === 0 || overLimit) ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                {zipping ? `Downloading ${zipProgress.done} of ${zipProgress.total}…` : `Download selected${chosen.length ? ` (${chosen.length})` : ''}`}
              </button>
            </div>
            {zipMsg && <div style={{ fontSize: '12px', color: zipMsg.startsWith('✓') ? '#059669' : '#b45309', marginBottom: '10px' }}>{zipMsg}</div>}

            {/* file list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {files.map((f) => (
                <div key={f.id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px' }}>
                  <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggle(f.id)} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '14px' }}>{f.file_name}</span>
                  <span style={{ color: '#94a3b8', fontSize: '12px' }}>{fmtSize(f.size_bytes)}</span>
                  <button onClick={() => download(f)} disabled={!!busy[f.id]}
                    style={{ background: busy[f.id] ? '#94a3b8' : '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', padding: '7px 14px', fontSize: '13px', fontWeight: 600, cursor: busy[f.id] ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                    {busy[f.id] ? 'Preparing…' : 'Download'}
                  </button>
                </div>
                {fileMsg[f.id] && <div style={{ padding: '0 12px 10px', fontSize: '12px', color: '#b45309' }}>{fileMsg[f.id]}</div>}
                </div>
              ))}
            </div>
          </>
        )}
        <div style={{ marginTop: '20px', fontSize: '12px', color: '#94a3b8' }}>Downloads are logged. This link may expire or be withdrawn.</div>
      </div>
    </div>
  );
}
